import React from 'react';
import { Link } from 'react-router-dom';
import { useList, useOne } from '@refinedev/core';
import { Button, Collapse, Descriptions, Space, Table, Tag, theme } from 'antd';
import {
  AdminSurveyResponseDetailDto,
  AdminSurveyResponseListItemDto,
  ResponseStatus,
} from '@wellverse/shared-ts';

const statusColors: Record<ResponseStatus, string> = {
  [ResponseStatus.IN_PROGRESS]: '#f5a623',
  [ResponseStatus.COMPLETED]: '#3ecf8e',
  [ResponseStatus.ABANDONED]: '#ff6b6b',
};

/**
 * Renders an answer the way it was collected. The onboarding survey stores
 * multi-selects as a JSON array (`equipment_owned`) and meal times as a JSON
 * object, so a bare `String(value)` would print `[object Object]`.
 */
const formatAnswer = (value: string): string => {
  if (!value) return '—';
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return value;
  }
  if (Array.isArray(parsed)) return parsed.join(', ');
  if (parsed && typeof parsed === 'object') {
    return Object.entries(parsed as Record<string, unknown>)
      .map(([key, entry]) => `${key}: ${entry}`)
      .join(' · ');
  }
  return String(parsed);
};

/**
 * The user's onboarding survey run: progress plus every raw answer.
 *
 * `UserProfile` above only keeps the fields onboarding maps onto it, so the
 * rest of the survey (experience, equipment, meal times, permission opt-ins,
 * referral code) is only visible here.
 */
export const UserOnboardingResponse: React.FC<{ userId: string }> = ({ userId }) => {
  const { token } = theme.useToken();

  const { data: list, isLoading: isListLoading } = useList<AdminSurveyResponseListItemDto>({
    resource: 'survey-responses',
    filters: [{ field: 'userId', operator: 'eq', value: userId }],
    pagination: { pageSize: 10 },
  });

  // The endpoint orders by `startedAt` desc, so the first row is the latest run.
  const summary = list?.data?.[0];

  const { data: detail, isLoading: isDetailLoading } = useOne<AdminSurveyResponseDetailDto>({
    resource: 'survey-responses',
    id: summary?.id ?? '',
    queryOptions: { enabled: Boolean(summary?.id) },
  });

  if (isListLoading) return null;

  if (!summary) {
    return (
      <Collapse
        style={{ marginTop: 24 }}
        items={[
          {
            key: 'onboarding',
            label: 'Onboarding Survey',
            children: (
              <div style={{ color: token.colorTextSecondary }}>
                This user has not started an onboarding survey.
              </div>
            ),
          },
        ]}
      />
    );
  }

  const response = detail?.data;
  const statusColor = statusColors[summary.status] || '#898989';

  return (
    <Collapse
      style={{ marginTop: 24 }}
      items={[
        {
          key: 'onboarding',
          label: (
            <Space>
              Onboarding Survey
              <Tag
                style={{
                  borderColor: statusColor,
                  color: statusColor,
                  background: `${statusColor}15`,
                  borderRadius: 9999,
                  padding: '0 10px',
                  fontWeight: 500,
                }}
              >
                {summary.status}
              </Tag>
              <span style={{ color: token.colorTextSecondary, fontWeight: 400 }}>
                {summary.answerCount} answers
              </span>
            </Space>
          ),
          children: (
            <>
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label="Survey">{summary.surveyName}</Descriptions.Item>
                <Descriptions.Item label="Screen Reached">
                  {summary.currentScreenIndex}
                </Descriptions.Item>
                <Descriptions.Item label="Started At">
                  {new Date(summary.startedAt).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Completed At">
                  {summary.completedAt ? new Date(summary.completedAt).toLocaleString() : '—'}
                </Descriptions.Item>
              </Descriptions>

              <Table
                style={{ marginTop: 16 }}
                dataSource={response?.answers ?? []}
                loading={isDetailLoading}
                rowKey="id"
                pagination={false}
                size="small"
              >
                <Table.Column dataIndex="questionKey" title="Question Key" width={200} />
                <Table.Column dataIndex="questionText" title="Question" />
                <Table.Column
                  dataIndex="answerValue"
                  title="Answer"
                  render={(value: string) => formatAnswer(value)}
                />
                <Table.Column
                  dataIndex="answeredAt"
                  title="Answered At"
                  width={180}
                  render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
                />
              </Table>

              <Link to={`/survey-responses/show/${summary.id}`}>
                <Button type="default" style={{ marginTop: 16 }}>
                  Open Full Response
                </Button>
              </Link>
            </>
          ),
        },
      ]}
    />
  );
};
